/**
 * ShareBar UI Component
 * Renders the share button bar
 */

import type { Platform, ShareContent, PositionMode, PlatformId, LayoutMode, BlockShareConfig, BlockItemConfig, LabelState, ButtonShape } from '../types';

export interface ShareBarConfig {
  platforms: Platform[];
  labels: Record<string, string>;
  onButtonClick: (platform: Platform, event: MouseEvent) => void;
  mode?: LayoutMode;
  shape?: ButtonShape;
  blockConfig?: BlockShareConfig;
}

/** Create the check icon SVG for copy success */
function createCheckIcon(): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>`;
}

/** Create a share button element */
function createButton(
  platform: Platform,
  label: string,
  content: ShareContent,
  onClick: (platform: Platform, event: MouseEvent) => void,
  mode: LayoutMode = 'icon',
  blockConfig?: BlockShareConfig
): HTMLElement {
  const url = platform.buildUrl(content);
  const isLink = url !== null;
  
  const element = document.createElement(isLink ? 'a' : 'button');
  element.className = `social-share-btn social-share-btn-${platform.id}`;
  element.setAttribute('data-platform', platform.id);
  element.setAttribute('title', label);
  element.setAttribute('aria-label', label);
  
  if (isLink && element instanceof HTMLAnchorElement) {
    element.href = url;
    element.target = '_blank';
    element.rel = 'noopener noreferrer';
  }
  
  // Handle block mode specific attributes
  if (mode === 'block') {
    const itemConfig: BlockItemConfig = blockConfig?.items?.[platform.id] || {};
    const grow = itemConfig.grow !== false; // Default to true
    const labelState: LabelState = itemConfig.labelState || 'sm';
    
    // Set flex-grow via inline style only for explicit grow: false
    // Let CSS handle the default growing behavior
    if (!grow) {
      element.style.flexGrow = '0';
      element.style.flexBasis = 'auto';
    }
    
    // Set data attribute for label state (CSS uses this for granular responsive breakpoints)
    element.setAttribute('data-label-state', labelState);
  }
  
  // Icon
  const icon = document.createElement('span');
  icon.className = 'social-share-icon';
  icon.setAttribute('aria-hidden', 'true');
  element.appendChild(icon);
  
  // Add text label for block mode
  if (mode === 'block') {
    const itemConfig: BlockItemConfig = blockConfig?.items?.[platform.id] || {};
    const labelText = itemConfig.text || platform.name;
    
    const labelSpan = document.createElement('span');
    labelSpan.className = 'social-share-label';
    labelSpan.textContent = labelText;
    element.appendChild(labelSpan);
  }
  
  // Tooltip for copy link
  if (platform.id === 'copylink') {
    const tooltip = document.createElement('span');
    tooltip.className = 'social-share-tooltip';
    tooltip.innerHTML = `<span class="social-share-tooltip-success">${createCheckIcon()}<span>Link copied!</span></span>`;
    element.appendChild(tooltip);
  }
  
  // Click handler
  element.addEventListener('click', (e) => {
    onClick(platform, e as MouseEvent);
  });
  
  return element;
}

/** Create the share bar container */
export function createShareBar(
  config: ShareBarConfig,
  content: ShareContent,
  position: PositionMode
): HTMLElement {
  const container = document.createElement('div');
  const mode = config.mode || 'icon';
  const shape = config.shape || 'circle';
  
  // Build class list: base + position + layout mode + shape
  const classes = [
    'social-share-bar',
    `social-share-bar--${position}`,
    `social-share-bar--${mode}`,
    `social-share-bar--shape-${shape}`
  ];
  container.className = classes.join(' ');
  
  container.setAttribute('role', 'group');
  container.setAttribute('aria-label', 'Share buttons');

  // Calculate radius for this specific instance
  const isBlockMode = mode === 'block';
  let borderRadiusValue = '50%';
  
  if (shape === 'square') {
    borderRadiusValue = '0px';
  } else if (shape === 'pill') {
    borderRadiusValue = '9999px';
  } else if (shape === 'rounded') {
    borderRadiusValue = `${config.blockConfig?.radius ?? 8}px`;
  } else {
    // shape === 'circle'
    if (isBlockMode) {
      borderRadiusValue = `${config.blockConfig?.radius ?? 4}px`;
    } else {
      borderRadiusValue = '50%';
    }
  }

  // Inject CSS variables inline to isolate styles per instance
  container.style.setProperty('--ss-radius', borderRadiusValue);
  
  for (const platform of config.platforms) {
    const label = config.labels[platform.id] || platform.name;
    const button = createButton(platform, label, content, config.onButtonClick, mode, config.blockConfig);
    container.appendChild(button);
  }
  
  return container;
}

/** Update share bar position class */
export function updateShareBarPosition(
  element: HTMLElement,
  position: 'auto' | 'left' | 'bottom' | 'static'
): void {
  // Remove all position classes
  element.classList.remove(
    'social-share-bar--auto',
    'social-share-bar--left',
    'social-share-bar--bottom',
    'social-share-bar--static'
  );
  // Add new position class
  element.classList.add(`social-share-bar--${position}`);
}

/** Check and handle overflow for static position */
export function checkStaticOverflow(
  element: HTMLElement,
  platforms: Platform[],
  iconSize: number,
  buttonMargin: number
): void {
  // Only check for static position
  if (!element.classList.contains('social-share-bar--static')) return;
  
  // No-op: We are moving towards CSS-only flex wrapping for stability
  // Leaving function signature for backward compatibility if needed, 
  // but disabling the logic to prevent layout thrashing/INP issues.
  
  /*
  const container = element.parentElement;
  if (!container) return;
  
  // Calculate available width
  const containerWidth = container.clientWidth;
  const buttonsPerRow = Math.floor(
    (containerWidth + buttonMargin) / (iconSize + buttonMargin)
  );
  
  // Check if all buttons fit
  const buttons = element.querySelectorAll('.social-share-btn');
  const totalButtons = buttons.length;
  
  if (totalButtons > buttonsPerRow && buttonsPerRow > 0) {
    // Hide buttons that don't fit
    const platformsToHide = platforms.slice(buttonsPerRow);
    const hiddenNames = platformsToHide.map(p => p.name).join(', ');
    
    console.warn(
      `[SocialShare] Container too narrow: hiding ${platformsToHide.length} platform(s): ${hiddenNames}. ` +
      `Container width: ${containerWidth}px, needs: ${totalButtons * (iconSize + buttonMargin) - buttonMargin}px`
    );
    
    buttons.forEach((btn, index) => {
      if (index >= buttonsPerRow) {
        (btn as HTMLElement).style.display = 'none';
      }
    });
  }
  */
}

/** Update all share links with new content */
export function updateShareLinks(
  element: HTMLElement,
  platforms: Platform[],
  content: ShareContent
): void {
  for (const platform of platforms) {
    const button = element.querySelector(`[data-platform="${platform.id}"]`);
    if (button instanceof HTMLAnchorElement) {
      const url = platform.buildUrl(content);
      if (url) {
        button.href = url;
      }
    }
  }
}

/** Show tooltip on a button */
export function showTooltip(platformId: PlatformId, element: HTMLElement): void {
  const button = element.querySelector(`[data-platform="${platformId}"]`);
  const tooltip = button?.querySelector('.social-share-tooltip');
  if (tooltip) {
    tooltip.classList.add('social-share-tooltip--visible');
  }
}

/** Hide tooltip on a button */
export function hideTooltip(platformId: PlatformId, element: HTMLElement): void {
  const button = element.querySelector(`[data-platform="${platformId}"]`);
  const tooltip = button?.querySelector('.social-share-tooltip');
  if (tooltip) {
    tooltip.classList.remove('social-share-tooltip--visible');
  }
}
